import { Component, Input, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
	NgbActiveModal,
	NgbModal,
	NgbModalRef,
	NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import {
	Export,
	PluginType,
	GameData,
	RendererGameDefinitionService,
} from '@meta-press/shared-plugins';
import {
	MigrationService,
	MigrationResult,
} from '../migration-service/migration.service';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { NotificationService } from '../notification-service/notification.service';
import { ExportService } from '../export-service/export.service';
import { ExportNotificationModalComponent } from '../export-notification-modal/export-notification-modal.component';

export enum MigrationState {
	Initial = 'initial',
	Migrating = 'migrating',
	Success = 'success',
	Failed = 'failed',
}

export interface ExportCompatibilityResult {
	export: Export;
	isCompatible: boolean;
	migrationStatus: 'pending' | 'migrating' | 'success' | 'failed';
	error?: string;
	compatibilityError?: string; // Error from the initial parsing/compatibility check
	migratedExport?: Export;
}

@Component({
	selector: 'app-migration-modal',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbTooltipModule],
	template: `
		<div class="modal-header">
			<h4 class="modal-title">
				<i class="bi bi-tools me-2"></i>
				Pelien muunnos
			</h4>
			<button
				type="button"
				class="btn-close"
				aria-label="Close"
				(click)="activeModal.dismiss('Cross click')"
			></button>
		</div>

		<div class="modal-body">
			<!-- Summary info -->
			<div class="alert alert-info mb-4" role="alert">
				<div class="d-flex justify-content-between align-items-center">
					<div>
						<i class="bi bi-info-circle me-2"></i>
						<strong>Yhteenveto:</strong>
						{{ totalCount }} peliä tarkistettu,
						{{ compatibleCount }} yhteensopivaa,
						{{ incompatibleExports.length }} vaatii muunnosta
					</div>
				</div>
			</div>

			<!-- List of incompatible exports -->
			<div *ngIf="incompatibleExports.length > 0">
				<h5 class="mb-3">
					<i
						class="bi bi-exclamation-triangle-fill text-warning me-2"
					></i>
					Muunnosta vaativat pelit
				</h5>

				<div class="list-group">
					<div
						*ngFor="
							let result of incompatibleExports;
							let i = index
						"
						class="list-group-item"
					>
						<div class="row align-items-center">
							<!-- Export info -->
							<div class="col-md-6">
								<div class="d-flex align-items-center">
									<!-- Status icon -->
									<div class="me-3">
										<i
											*ngIf="
												result.migrationStatus ===
												'pending'
											"
											class="bi bi-clock text-warning"
											title="Odottaa muunnosta"
										></i>
										<i
											*ngIf="
												result.migrationStatus ===
												'migrating'
											"
											class="bi bi-arrow-repeat text-primary spin"
											title="Muunnetaan..."
										></i>
										<i
											*ngIf="
												result.migrationStatus ===
												'success'
											"
											class="bi bi-check-circle-fill text-success"
											title="Muunnos onnistui"
										></i>
										<i
											*ngIf="
												result.migrationStatus ===
												'failed'
											"
											class="bi bi-x-circle-fill text-danger"
											title="Muunnos epäonnistui"
										></i>
									</div>

									<!-- Export details -->
									<div>
										<h6 class="mb-1">
											{{ result.export.name }}
											<!-- Info button with error details for incompatible exports -->
											<button
												*ngIf="
													!result.isCompatible &&
													result.compatibilityError
												"
												type="button"
												class="btn btn-sm btn-outline-info ms-2 p-1"
												style="font-size: 0.7rem; line-height: 1;"
												[ngbTooltip]="
													result.compatibilityError
												"
												placement="top"
											>
												<i
													class="bi bi-info-circle"
												></i>
											</button>
										</h6>
										<small class="text-muted">
											Tyyppi:
											{{
												result.export.gameData?.type ||
													'Tuntematon'
											}}
											<span
												*ngIf="result.export.date"
												class="ms-2"
											>
												|
												{{
													result.export.date * 1000
														| date: 'short'
												}}
											</span>
										</small>
									</div>
								</div>
							</div>

							<!-- Action buttons -->
							<div class="col-md-6 text-end">
								<div class="btn-group" role="group">
									<!-- Fix button -->
									<button
										*ngIf="
											result.migrationStatus ===
												'pending' ||
											result.migrationStatus === 'failed'
										"
										class="btn btn-sm btn-outline-primary"
										(click)="
											startMigrationForExport(result, i)
										"
										[disabled]="isAnyMigrationInProgress()"
									>
										<i class="bi bi-arrow-repeat me-1"></i>
										{{
											result.migrationStatus === 'failed'
												? 'Yritä uudelleen'
												: 'Korjaa'
										}}
									</button>

									<!-- Preview button (only for successful migrations) -->
									<button
										*ngIf="
											result.migrationStatus ===
												'success' &&
											result.migratedExport
										"
										class="btn btn-sm btn-outline-success"
										(click)="previewMigratedExport(result)"
									>
										<i class="bi bi-eye me-1"></i>
										Esikatsele
									</button>

									<!-- Save buttons (only for successful migrations) -->
									<div
										*ngIf="
											result.migrationStatus ===
												'success' &&
											result.migratedExport
										"
										class="btn-group ms-2"
									>
										<button
											class="btn btn-sm btn-success"
											(click)="overwriteOriginal(result)"
											title="Korvaa alkuperäinen peli muunnetulla versiolla"
										>
											<i class="bi bi-save me-1"></i>
											Korvaa
										</button>
										<button
											class="btn btn-sm btn-outline-success"
											(click)="saveAsNew(result)"
											title="Tallenna muunnettu peli uutena"
										>
											<i
												class="bi bi-plus-square me-1"
											></i>
											Tallenna uutena
										</button>
									</div>
								</div>
							</div>
						</div>

						<!-- Error message -->
						<div
							*ngIf="
								result.migrationStatus === 'failed' &&
								result.error
							"
							class="mt-2 alert alert-sm alert-danger mb-0"
						>
							<small>{{ result.error }}</small>
						</div>

						<!-- Migration progress -->
						<div
							*ngIf="result.migrationStatus === 'migrating'"
							class="mt-2"
						>
							<div class="progress" style="height: 4px;">
								<div
									class="progress-bar progress-bar-striped progress-bar-animated"
									role="progressbar"
									style="width: 100%"
								></div>
							</div>
							<small class="text-muted">Muunnetaan...</small>
						</div>
					</div>
				</div>
			</div>

			<!-- No incompatible exports message -->
			<div
				*ngIf="incompatibleExports.length === 0"
				class="text-center py-4"
			>
				<i
					class="bi bi-check-circle-fill text-success"
					style="font-size: 3rem;"
				></i>
				<h5 class="mt-3">Kaikki pelit ovat yhteensopivia!</h5>
				<p class="text-muted">Ei tarvetta muunnoksille.</p>
			</div>
		</div>

		<div class="modal-footer">
			<button
				type="button"
				class="btn btn-secondary"
				(click)="activeModal.dismiss('Cancel')"
				[disabled]="isAnyMigrationInProgress()"
			>
				Sulje
			</button>

			<button
				*ngIf="incompatibleExports.length > 0"
				type="button"
				class="btn btn-primary"
				(click)="startMigrationForAll()"
				[disabled]="
					isAnyMigrationInProgress() || allMigrationsCompleted()
				"
			>
				<i class="bi bi-arrow-repeat me-2"></i>
				Korjaa kaikki
			</button>
		</div>
	`,
	styles: [
		`
			.modal-body {
				min-height: 200px;
			}
			.spin {
				animation: spin 1s linear infinite;
			}
			@keyframes spin {
				from {
					transform: rotate(0deg);
				}
				to {
					transform: rotate(360deg);
				}
			}
		`,
	],
})
export class MigrationModalComponent {
	@Input() export!: Export; // Legacy support
	@Input() compatibilityResults: ExportCompatibilityResult[] = [];

	state = MigrationState.Initial;
	MigrationState = MigrationState; // Make enum available in template

	migratedExport?: Export;
	errorMessage = '';

	// Get only incompatible exports for display
	get incompatibleExports(): ExportCompatibilityResult[] {
		return this.compatibilityResults.filter(
			(result) => !result.isCompatible,
		);
	}

	// Get compatible exports count for display
	get compatibleCount(): number {
		return this.compatibilityResults.filter((result) => result.isCompatible)
			.length;
	}

	// Get total exports count
	get totalCount(): number {
		return this.compatibilityResults.length;
	}

	constructor(
		public activeModal: NgbActiveModal,
		private migrationService: MigrationService,
		private modalService: NgbModal,
		private sourceMaterialService: SourceMaterialService,
		private notificationService: NotificationService,
		private exportService: ExportService,
		private rendererGameDefinitionService: RendererGameDefinitionService,
	) {}

	startMigration(): void {
		this.state = MigrationState.Migrating;
		this.errorMessage = '';

		this.migrationService.migrateGameData(this.export).subscribe({
			next: (result: MigrationResult) => {
				if (result.success && result.migratedExport) {
					this.state = MigrationState.Success;
					this.migratedExport = result.migratedExport;
				} else {
					this.state = MigrationState.Failed;
					this.errorMessage =
						result.error || 'Tuntematon virhe muunnoksessa';
				}
			},
			error: (error) => {
				this.state = MigrationState.Failed;
				this.errorMessage = 'Verkkovirhe muunnoksen aikana';
				console.error('Migration error:', error);
			},
		});
	}

	retryMigration(): void {
		this.startMigration();
	}

	async playMigratedGame(): Promise<void> {
		try {
			if (!this.migratedExport || !this.migratedExport.gameData) {
				this.notificationService.ShowToast(
					'Muunnettua peliä ei löydy',
					'danger',
				);
				return;
			}

			console.log('[MigrationModal] DEBUG: Starting playMigratedGame...');
			console.log(
				'[MigrationModal] DEBUG: Migrated GameData structure:',
				JSON.stringify(this.migratedExport.gameData, null, 2),
			);

			// Ensure the migrated game data has proper methods
			console.log(
				'[MigrationModal] DEBUG: Creating RendererGameDefinitionService...',
			);
			const rendererGameDefinitionService =
				new RendererGameDefinitionService();
			console.log(
				'[MigrationModal] DEBUG: RendererGameDefinitionService created',
			);

			console.log('[MigrationModal] DEBUG: Getting game type...');
			const gameType = this.migratedExport.gameData.type;
			console.log('[MigrationModal] DEBUG: Game type:', gameType);

			const gameDefinition =
				await rendererGameDefinitionService.getGameDefinition(gameType);

			if (!gameDefinition) {
				console.error(
					'[MigrationModal] ERROR: Game definition not found for type:',
					gameType,
				);
				this.notificationService.ShowToast(
					'Pelityyppiä ei löydy',
					'danger',
				);
				return;
			}

			console.log(
				'[MigrationModal] DEBUG: Game definition found:',
				gameDefinition.constructor.name,
			);

			// Add methods to the game data
			console.log(
				'[MigrationModal] DEBUG: Adding methods to main GameData...',
			);
			GameData.addMethods(
				this.migratedExport.gameData,
				gameDefinition.gameDataType,
			);
			console.log('[MigrationModal] DEBUG: Main GameData methods added');

			// Build map of all game data types for nested objects
			console.log(
				'[MigrationModal] DEBUG: Building GameData types map...',
			);

			const availableTypes =
				await rendererGameDefinitionService.getAvailableGameTypes();

			console.log(
				'[MigrationModal] DEBUG: Available game types:',
				availableTypes.map((t) => ({ type: t.type, title: t.title })),
			);

			// Add prototypes to all nested GameData objects
			console.log(
				'[MigrationModal] DEBUG: Adding prototypes to all nested GameData objects...',
			);
			console.log(
				'[MigrationModal] DEBUG: Before addPrototypesToAllGameData - checking nested object:',
			);
			const testParagraphGameData = (this.migratedExport.gameData as any)
				.topics?.[0]?.paragraphs?.[0]?.gameData;
			if (testParagraphGameData) {
				console.log(
					'[MigrationModal] DEBUG: Nested gameData before prototype addition:',
					{
						type: testParagraphGameData.type,
						constructor: testParagraphGameData.constructor.name,
						hasGetUsedArticles:
							typeof testParagraphGameData.GetUsedArticles ===
							'function',
						hasGetContainedLocalGameDataObjects:
							typeof testParagraphGameData.GetContainedLocalGameDataObjects ===
							'function',
						prototype: Object.getPrototypeOf(testParagraphGameData)
							.constructor.name,
					},
				);
			}

			try {
				// Build the GameData types map by loading constructors for all available types
				const availableTypes = this.rendererGameDefinitionService.AvailablePluginTypes;
				const gameDataTypesMap = new Map<PluginType, new () => GameData>();
				
				for (const pluginType of availableTypes) {
					try {
						const constructor = await this.rendererGameDefinitionService.getGameDataConstructor(pluginType);
						if (constructor) {
							gameDataTypesMap.set(pluginType, constructor);
						}
					} catch (error) {
						console.error(`[MigrationModal] Failed to load constructor for type ${pluginType}:`, error);
					}
				}
				
				this.migratedExport.gameData.addPrototypesToAllGameData(
					gameDataTypesMap,
				);
				console.log(
					'[MigrationModal] DEBUG: Prototypes added successfully',
				);

				// Check the nested object again after prototype addition
				if (testParagraphGameData) {
					console.log(
						'[MigrationModal] DEBUG: Nested gameData after prototype addition:',
						{
							type: testParagraphGameData.type,
							constructor: testParagraphGameData.constructor.name,
							hasGetUsedArticles:
								typeof testParagraphGameData.GetUsedArticles ===
								'function',
							hasGetContainedLocalGameDataObjects:
								typeof testParagraphGameData.GetContainedLocalGameDataObjects ===
								'function',
							prototype: Object.getPrototypeOf(
								testParagraphGameData,
							).constructor.name,
						},
					);
				}
			} catch (prototypeError) {
				console.error(
					'[MigrationModal] ERROR: Failed to add prototypes:',
					prototypeError,
				);
				this.notificationService.ShowToast(
					`Prototyyppien lisääminen epäonnistui: ${prototypeError}`,
					'danger',
				);
				return;
			}

			console.log(
				'[MigrationModal] DEBUG: Getting game type settings...',
			);
			const gameTypeDefinition =
				this.sourceMaterialService.GetGameTypeSettings(
					this.migratedExport.gameData.type,
				);
			console.log(
				'[MigrationModal] DEBUG: Game type definition found:',
				!!gameTypeDefinition,
			);
			console.log(
				'[MigrationModal] DEBUG: Game type definition details:',
				gameTypeDefinition,
			);

			// Test the methods before proceeding to modal opening
			console.log('[MigrationModal] DEBUG: Testing methods...');
			try {
				const usedArticles =
					this.migratedExport.gameData.GetUsedArticles();
				console.log(
					'[MigrationModal] DEBUG: Main GetUsedArticles returned:',
					usedArticles,
				);

				const containedObjects =
					this.migratedExport.gameData.GetContainedLocalGameDataObjects();
				console.log(
					'[MigrationModal] DEBUG: Found',
					containedObjects.length,
					'contained objects',
				);

				containedObjects.forEach((obj: GameData, index: number) => {
					console.log(
						`[MigrationModal] DEBUG: Testing contained object ${index}:`,
						{
							type: obj.type,
							constructor: obj.constructor.name,
							hasGetUsedArticles:
								typeof obj.GetUsedArticles === 'function',
							hasGetContainedLocalGameDataObjects:
								typeof obj.GetContainedLocalGameDataObjects ===
								'function',
						},
					);

					try {
						const nestedArticles = obj.GetUsedArticles();
						console.log(
							`[MigrationModal] DEBUG: Contained object ${index} GetUsedArticles returned:`,
							nestedArticles,
						);
					} catch (nestedError) {
						console.error(
							`[MigrationModal] ERROR: Contained object ${index} failed GetUsedArticles:`,
							nestedError,
						);
						throw nestedError;
					}
				});
			} catch (methodError) {
				console.error(
					'[MigrationModal] ERROR: Method testing failed:',
					methodError,
				);
				this.notificationService.ShowToast(
					`Metodien testaaminen epäonnistui: ${methodError}`,
					'danger',
				);
				return;
			}

			if (gameTypeDefinition && gameTypeDefinition.rendererComponent) {
				console.log(
					'[MigrationModal] DEBUG: Renderer component exists, loading...',
				);
				try {
					console.log(
						'[MigrationModal] DEBUG: Calling rendererComponent()...',
					);
					const componentType =
						await gameTypeDefinition.rendererComponent();
					console.log(
						'[MigrationModal] DEBUG: Component type loaded:',
						componentType,
					);

					console.log('[MigrationModal] DEBUG: Opening modal...');
					const modalRef = this.modalService.open(componentType, {
						size: 'xl',
						windowClass: `dark-modal ${
							gameTypeDefinition.displayMode === 'fullscreen' ||
							(gameTypeDefinition.displayMode === 'both' &&
								gameTypeDefinition.defaultDisplayMode ===
									'fullscreen')
								? 'modal-xxl'
								: ''
						}`,
					});
					console.log(
						'[MigrationModal] DEBUG: Modal opened:',
						!!modalRef,
					);

					if (modalRef && modalRef.componentInstance) {
						console.log(
							'[MigrationModal] DEBUG: Setting exportData to modal component...',
						);
						modalRef.componentInstance.exportData =
							this.migratedExport;
						console.log(
							'[MigrationModal] DEBUG: Export data set successfully',
						);
					} else {
						console.warn(
							'[MigrationModal] WARNING: Modal or component instance not found',
						);
					}
				} catch (error) {
					console.error(
						'[MigrationModal] ERROR: Error loading game component:',
						error,
					);
					this.notificationService.ShowToast(
						'Pelin lataaminen epäonnistui',
						'danger',
					);
				}
			} else {
				console.error(
					'[MigrationModal] ERROR: Game type definition not found or missing renderer component',
				);
				console.error(
					'[MigrationModal] ERROR: Game type:',
					this.migratedExport.gameData.type,
				);
				console.error(
					'[MigrationModal] ERROR: Definition found:',
					!!gameTypeDefinition,
				);
				console.error(
					'[MigrationModal] ERROR: Has renderer component:',
					gameTypeDefinition?.rendererComponent ? 'yes' : 'no',
				);

				this.notificationService.ShowToast(
					`Peliä ei voida avata: pelityyppi ${this.migratedExport.gameData.type} ei ole tuettu`,
					'danger',
				);
			}
		} catch (error) {
			console.error(
				'[MigrationModal] ERROR: Critical error in playMigratedGame:',
				error,
			);
			this.notificationService.ShowToast(
				`Kriittinen virhe: ${error}`,
				'danger',
			);
		}
	}

	/**
	 * Save migrated export as new with user-provided name
	 */
	saveAsNew(result: ExportCompatibilityResult): void {
		if (!result.migratedExport) {
			this.notificationService.ShowToast(
				'Muunnettu peli puuttuu',
				'danger',
			);
			return;
		}

		// Open export notification modal for name input
		const modalRef = this.modalService.open(
			ExportNotificationModalComponent,
			{
				centered: true,
				backdrop: 'static',
				keyboard: false,
			},
		);

		if (modalRef.componentInstance) {
			modalRef.componentInstance.showNamePrompt = true;
			modalRef.componentInstance.exportName = `${result.export.name} (muunnettu)`;

			// Subscribe to name submission
			modalRef.componentInstance.nameSubmitted.subscribe(
				(name: string) => {
					this.performSaveAsNew(result, name, modalRef);
				},
			);
		}
	}

	/**
	 * Perform the actual save as new operation
	 */
	private performSaveAsNew(
		result: ExportCompatibilityResult,
		name: string,
		modalRef: NgbModalRef,
	): void {
		if (!result.migratedExport) {
			return;
		}

		// Create new export with provided name and no ID (will be assigned by server)
		const newExport: Export = {
			...result.migratedExport,
			id: -1, // Reset ID to create new
			name: name,
			date: Math.floor(Date.now() / 1000), // Current timestamp
		};

		// Save the new export
		this.exportService.SaveExport(newExport).subscribe({
			next: (savedExport) => {
				console.log(
					'[MigrationModal] Save as new successful:',
					savedExport,
				);
				modalRef.componentInstance.updateState(false, true, false);
				this.notificationService.ShowToast(
					`Peli "${name}" tallennettu onnistuneesti!`,
					'success',
				);

				// Close the modal after a short delay
				setTimeout(() => {
					modalRef.close();
				}, 1500);
			},
			error: (error) => {
				console.error('[MigrationModal] Save as new failed:', error);
				modalRef.componentInstance.updateState(false, false, true);
				this.notificationService.ShowToast(
					'Pelin tallentaminen epäonnistui',
					'danger',
				);
			},
		});
	}

	/**
	 * Overwrite original export with migrated version
	 */
	overwriteOriginal(result: ExportCompatibilityResult): void {
		if (
			!result.migratedExport ||
			!result.export.id ||
			result.export.id === -1
		) {
			this.notificationService.ShowToast(
				'Virhe: alkuperäinen peli tai muunnettu peli puuttuu',
				'danger',
			);
			return;
		}

		// Update the migrated export with original export's metadata
		const updatedExport: Export = {
			...result.migratedExport,
			id: result.export.id,
			name: result.export.name,
			creator: result.export.creator,
			date: result.export.date, // Keep original date
		};

		// Update the export
		this.exportService.UpdateExport(updatedExport).subscribe({
			next: (savedExport) => {
				console.log(
					'[MigrationModal] Overwrite successful:',
					savedExport,
				);
				this.notificationService.ShowToast(
					`Peli "${result.export.name}" korvattu muunnetulla versiolla!`,
					'success',
				);
			},
			error: (error) => {
				console.error('[MigrationModal] Overwrite failed:', error);
				this.notificationService.ShowToast(
					'Pelin korvaaminen epäonnistui',
					'danger',
				);
			},
		});
	}

	/**
	 * Start migration for a specific export
	 */
	async startMigrationForExport(
		result: ExportCompatibilityResult,
		index: number,
	): Promise<void> {
		console.log(
			`[MigrationModal] Starting migration for export ${index}:`,
			result.export.name,
		);

		// Update status to migrating
		result.migrationStatus = 'migrating';
		result.error = undefined;

		try {
			const migrationResult = await this.migrationService
				.migrateGameData(result.export)
				.toPromise();

			if (
				migrationResult &&
				migrationResult.success &&
				migrationResult.migratedExport
			) {
				result.migrationStatus = 'success';
				result.migratedExport = migrationResult.migratedExport;
				console.log(
					`[MigrationModal] Migration successful for export ${index}`,
				);
			} else {
				result.migrationStatus = 'failed';
				result.error =
					migrationResult?.error || 'Tuntematon virhe muunnoksessa';
				console.error(
					`[MigrationModal] Migration failed for export ${index}:`,
					result.error,
				);
			}
		} catch (error) {
			result.migrationStatus = 'failed';
			result.error = 'Verkkovirhe muunnoksen aikana';
			console.error(
				`[MigrationModal] Migration error for export ${index}:`,
				error,
			);
		}
	}

	/**
	 * Preview a migrated export
	 */
	async previewMigratedExport(
		result: ExportCompatibilityResult,
	): Promise<void> {
		if (!result.migratedExport) {
			this.notificationService.ShowToast(
				'Muunnettua peliä ei löydy',
				'danger',
			);
			return;
		}

		console.log(
			'[MigrationModal] Previewing migrated export:',
			result.export.name,
		);

		// Set the migrated export as the current one for preview
		this.migratedExport = result.migratedExport;

		// Use the existing playMigratedGame method
		await this.playMigratedGame();
	}

	/**
	 * Start migration for all incompatible exports
	 */
	async startMigrationForAll(): Promise<void> {
		console.log(
			'[MigrationModal] Starting migration for all incompatible exports',
		);

		const incompatibleExports = this.incompatibleExports.filter(
			(result) =>
				result.migrationStatus === 'pending' ||
				result.migrationStatus === 'failed',
		);

		if (incompatibleExports.length === 0) {
			this.notificationService.ShowToast(
				'Ei muunnettavia pelejä',
				'primary',
			);
			return;
		}

		// Process migrations sequentially to avoid overwhelming the system
		for (let i = 0; i < incompatibleExports.length; i++) {
			const result = incompatibleExports[i];
			const originalIndex = this.incompatibleExports.indexOf(result);

			console.log(
				`[MigrationModal] Processing migration ${i + 1}/${incompatibleExports.length} for:`,
				result.export.name,
			);
			await this.startMigrationForExport(result, originalIndex);

			// Small delay between migrations
			await new Promise((resolve) => setTimeout(resolve, 500));
		}

		const successCount = incompatibleExports.filter(
			(r) => r.migrationStatus === 'success',
		).length;
		const failedCount = incompatibleExports.filter(
			(r) => r.migrationStatus === 'failed',
		).length;

		if (failedCount === 0) {
			this.notificationService.ShowToast(
				`Kaikki ${successCount} peliä muunnettu onnistuneesti!`,
				'success',
			);
		} else {
			this.notificationService.ShowToast(
				`${successCount} peliä muunnettu, ${failedCount} epäonnistui`,
				'warning',
			);
		}
	}

	/**
	 * Check if any migration is currently in progress
	 */
	isAnyMigrationInProgress(): boolean {
		return this.incompatibleExports.some(
			(result) => result.migrationStatus === 'migrating',
		);
	}

	/**
	 * Check if all migrations have been completed (either success or failed)
	 */
	allMigrationsCompleted(): boolean {
		return (
			this.incompatibleExports.length > 0 &&
			this.incompatibleExports.every(
				(result) =>
					result.migrationStatus === 'success' ||
					result.migrationStatus === 'failed',
			)
		);
	}
}
